import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Lock,
  AlertCircle,
  CheckCircle2,
  Clock,
  Send,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import { accessRequestService } from '../../services/accessRequest.service';
import caseService from '../../services/case.service';
import type { ConfidentialDocument } from '../../types/accessRequest.types';
import { formatDocumentDisplayName } from '../../utils/documentName';
import { ROUTES } from '../../config/routes.config';

function getTodayString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const requestSchema = z
  .object({
    reason: z
      .string()
      .min(10, 'Please provide a reason (minimum 10 characters)')
      .max(500, 'Reason too long'),
    access_from_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    access_to_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  })
  .superRefine((data, ctx) => {
    const todayStr = getTodayString();
    const parse = (value: string) =>
      new Date(`${value}T00:00:00`);

    const todayDate = parse(todayStr);
    const fromDate = parse(data.access_from_date);
    const toDate = parse(data.access_to_date);

    if (fromDate < todayDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Access From date cannot be earlier than today',
        path: ['access_from_date'],
      });
    }
    if (toDate < todayDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Access To date cannot be earlier than today',
        path: ['access_to_date'],
      });
    }
    if (toDate < fromDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Access To date must be on or after Access From date',
        path: ['access_to_date'],
      });
    }
  });

type RequestForm = z.infer<typeof requestSchema>;

const AccessBadge: React.FC<{
  status: 'pending' | 'approved' | 'none';
}> = ({ status }) => {
  const config = {
    pending: {
      bg: 'var(--color-warning-light)',
      color: 'var(--color-warning)',
      icon: <Clock size={12} />,
      label: 'Request Pending',
    },
    approved: {
      bg: 'var(--color-success-light)',
      color: 'var(--color-success)',
      icon: <CheckCircle2 size={12} />,
      label: 'Access Granted',
    },
    none: {
      bg: 'var(--color-gray-100)',
      color: 'var(--color-gray-600)',
      icon: <Lock size={12} />,
      label: 'No Access',
    },
  }[status];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 10px',
        borderRadius: 'var(--radius-full)',
        background: config.bg,
        color: config.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semibold)',
      }}
    >
      {config.icon} {config.label}
    </span>
  );
};

interface RequestModalProps {
  document: ConfidentialDocument;
  onClose: () => void;
  onSuccess: () => void;
}

const RequestAccessModal: React.FC<RequestModalProps> = ({
  document,
  onClose,
  onSuccess,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RequestForm>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      access_from_date: getTodayString(),
      access_to_date: getTodayString(),
      // (() => {
      //   const d = new Date();
      //   d.setFullYear(d.getFullYear() + 1);
      //   return d.toISOString().slice(0, 10);
      // })(),
    },
  });

  const reasonValue = watch('reason', '');
 // const fromDateValue = watch('access_from_date', getTodayString());
 const fromDateValue = watch('access_from_date') ?? getTodayString();

  const submitMutation = useMutation({
    mutationFn: (data: RequestForm) =>
      accessRequestService.submitRequest(document.document_id, {
        reason: data.reason,
        access_from_date: data.access_from_date,
        access_to_date: data.access_to_date,
      }),
    onSuccess: () => {
      toast.success('Access request submitted successfully');
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      if (err.response?.status === 409) {
        toast.error(
          'You already have a pending request for this document'
        );
      } else {
        const msg =
          err.response?.data?.message || 'Failed to submit request';
        toast.error(msg);
      }
    },
  });

  const onSubmit = handleSubmit((data) => submitMutation.mutate(data));

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.document.addEventListener('keydown', handler);
    return () => window.document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9998,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-access-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: 520,
          padding: 'var(--spacing-6)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacing-5)',
          }}
        >
          <div>
            <h2
              id="request-access-title"
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-bold)',
                marginBottom: '4px',
              }}
            >
              Request Access
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-gray-500)',
              }}
            >
              Request access to this confidential document. The request will go to your division&apos;s content manager.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-gray-400)',
              padding: '4px',
            }}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'var(--color-gray-50)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-gray-200)',
            marginBottom: 'var(--spacing-5)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-3)',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-error-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Lock size={16} style={{ color: 'var(--color-error)' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <p
                style={{
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatDocumentDisplayName({
                  cmsNumber: document.cms_number,
                  originalFileName: document.original_file_name,
                  documentType: document.document_type,
                })}
              </p>
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-gray-500)',
                }}
              >
                {[
                  (document as any).file_number || document.cms_number,
                  document.folder_type,
                  document.document_type,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--spacing-4)',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                Access From Date *
              </label>
              <input
                type="date"
                {...register('access_from_date')}
                min={getTodayString()}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: `1px solid ${
                    errors.access_from_date
                      ? 'var(--color-error)'
                      : 'var(--color-gray-200)'
                  }`,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'white',
                  outline: 'none',
                }}
              />
              {errors.access_from_date && (
                <p
                  style={{
                    color: 'var(--color-error)',
                    fontSize: 'var(--font-size-xs)',
                    marginTop: '4px',
                  }}
                >
                  {errors.access_from_date.message}
                </p>
              )}
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                Access To Date *
              </label>
              <input
                type="date"
                {...register('access_to_date')}
                min={fromDateValue}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: `1px solid ${
                    errors.access_to_date
                      ? 'var(--color-error)'
                      : 'var(--color-gray-200)'
                  }`,
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'white',
                  outline: 'none',
                }}
              />
              {errors.access_to_date && (
                <p
                  style={{
                    color: 'var(--color-error)',
                    fontSize: 'var(--font-size-xs)',
                    marginTop: '4px',
                  }}
                >
                  {errors.access_to_date.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              Reason for Access *
            </label>
            <textarea
              {...register('reason')}
              rows={4}
              placeholder="Explain why you need access to this document..."
              style={{
                width: '100%',
                padding: 'var(--spacing-3)',
                border: `1px solid ${
                  errors.reason ? 'var(--color-error)' : 'var(--color-gray-200)'
                }`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '4px',
              }}
            >
              {errors.reason ? (
                <p
                  style={{
                    color: 'var(--color-error)',
                    fontSize: 'var(--font-size-xs)',
                  }}
                >
                  {errors.reason.message}
                </p>
              ) : (
                <span />
              )}
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-gray-400)',
                }}
              >
                {(reasonValue ?? '').length}/500
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: 'var(--spacing-2) var(--spacing-5)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 'var(--radius-md)',
                background: 'white',
                color: 'var(--color-gray-700)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitMutation.isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-5)',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                opacity: submitMutation.isPending ? 0.7 : 1,
              }}
            >
              <Send size={14} />
              {submitMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const AccessRequestPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedDocument, setSelectedDocument] =
    useState<ConfidentialDocument | null>(null);
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const limit = 15;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['confidential-docs', page],
    queryFn: async () => {
      try {
        const res = await accessRequestService.getConfidentialDocuments({
          page,
          limit,
        });
        const raw = res.data?.data ?? res.data;
        return raw;
      } catch (err: any) {
        if (err.response?.status === 404) {
          // Fallback: backend may not have /documents/confidential-no-access.
          // Use cases search with classification=Confidential and merge with
          // access-status to get has_pending_request and has_approved_access.
          const [casesRes, statusRes] = await Promise.all([
            caseService.searchCases({
              classification: 'Confidential',
              limit: 50,
              page: 1,
            }),
            accessRequestService.getAccessStatus({ page: 1, limit: 500 }),
          ]);
          const cases =
            casesRes.data?.data?.items ?? casesRes.data?.items ?? [];
          const statusData = statusRes.data?.data ?? statusRes.data;
          const allRequests = statusData?.items ?? [];
          const pendingDocIds = new Set(
            allRequests
              .filter((r: any) => r.status === 'Pending')
              .map((r: any) => r.document_id)
          );
          const approvedDocIds = new Set(
            allRequests
              .filter((r: any) => r.status === 'Approved')
              .map((r: any) => r.document_id)
          );
          const documents: ConfidentialDocument[] = [];
          for (const c of cases) {
            const docs = (c as any).documents ?? [];
            for (const d of docs) {
              if (d.classification === 'Confidential') {
                documents.push({
                  document_id: d.document_id,
                  original_file_name: d.original_file_name ?? '',
                  case_id: (c as any).case_id,
                  // File No (new form) falls back to legacy CMS number.
                  cms_number: (c as any).fir_number || (c as any).cms_number || '',
                  rrms_ref_number: (c as any).rrms_ref_number ?? '',
                  folder_type: d.folder_type ?? '',
                  document_type: d.document_type ?? '',
                  classification: 'Confidential',
                  uploaded_by_name: (d as any).uploaded_by_name ?? '—',
                  created_at: d.created_at ?? new Date().toISOString(),
                  has_pending_request: pendingDocIds.has(d.document_id),
                  has_approved_access: approvedDocIds.has(d.document_id),
                });
              }
            }
          }
          const start = (page - 1) * limit;
          const paginated = documents.slice(start, start + limit);
          return {
            items: paginated,
            pagination: {
              total: documents.length,
              page,
              limit,
              totalPages: Math.ceil(documents.length / limit) || 1,
              hasNextPage: start + limit < documents.length,
              hasPrevPage: page > 1,
            },
          };
        }
        throw err;
      }
    },
  });

  const documents: ConfidentialDocument[] = data?.items ?? [];
  const pagination = data?.pagination;

  const getAccessStatus = (doc: ConfidentialDocument) => {
    if (doc.has_approved_access) return 'approved';
    if (doc.has_pending_request) return 'pending';
    return 'none';
  };

  useEffect(() => {
    document.title = 'RRMS | Access Request';
  }, []);

  const closeRequestModal = useCallback(() => {
    setSelectedDocument(null);
    if (searchParams.get('document_id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('document_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const requestedDocId = searchParams.get('document_id');
    if (!requestedDocId || !documents.length || selectedDocument) return;
    const doc = documents.find(
      (d) => String(d.document_id) === String(requestedDocId)
    );
    if (doc) {
      setSelectedDocument(doc);
    }
  }, [searchParams, documents, selectedDocument]);

  return (
    <div className="">
      <PageHeader
        title="Access Request"
        subtitle="Request access to confidential documents you need for your work"
      />

      <div
        style={{
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-info-light)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-info)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-5)',
        }}
      >
        <AlertCircle
          size={16}
          style={{
            color: 'var(--color-info)',
            flexShrink: 0,
            marginTop: 2,
          }}
        />
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-info)',
          }}
        >
          Documents marked as <strong>Confidential</strong> require approval
          from a Content Manager before you can view or download them. Submit a
          request with a valid reason and the Content Manager will review it.
        </p>
      </div>

      {isLoading && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-3)',
          }}
        >
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="card"
              style={{
                padding: 'var(--spacing-4)',
                display: 'flex',
                gap: 'var(--spacing-3)',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-gray-100)',
                }}
              />
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-2)',
                }}
              >
                <div
                  style={{
                    height: 14,
                    width: '60%',
                    background: 'var(--color-gray-100)',
                    borderRadius: 4,
                  }}
                />
                <div
                  style={{
                    height: 12,
                    width: '40%',
                    background: 'var(--color-gray-100)',
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div
          className="card"
          style={{
            padding: 'var(--spacing-10)',
            textAlign: 'center',
          }}
        >
          <AlertCircle
            size={36}
            style={{
              color: 'var(--color-error)',
              margin: '0 auto var(--spacing-3)',
            }}
          />
          <p
            style={{
              color: 'var(--color-error)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            Failed to load confidential documents
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && documents.length === 0 && (
        <div
          className="card"
          style={{
            padding: 'var(--spacing-16)',
            textAlign: 'center',
          }}
        >
          <CheckCircle2
            size={48}
            style={{
              color: 'var(--color-success)',
              margin: '0 auto var(--spacing-4)',
            }}
          />
          <p
            style={{
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-gray-700)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            All caught up
          </p>
          <p
            style={{
              color: 'var(--color-gray-400)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            No confidential documents require access requests at this time
          </p>
        </div>
      )}

      {!isLoading && !isError && documents.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-3)',
              marginBottom: 'var(--spacing-5)',
            }}
          >
            {documents.map((doc) => {
              const accessStatus = getAccessStatus(doc);
              return (
                <div
                  key={doc.document_id}
                  className="card"
                  style={{
                    padding: 'var(--spacing-4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-4)',
                    transition: 'box-shadow var(--transition-base)',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 'var(--radius-md)',
                      flexShrink: 0,
                      background: 'var(--color-error-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Lock
                      size={18}
                      style={{ color: 'var(--color-error)' }}
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-2)',
                        marginBottom: '4px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            ROUTES.CASE_DETAIL.replace(
                              ':caseId',
                              String(doc.case_id)
                            )
                          )
                        }
                        style={{
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          margin: 0,
                          cursor: 'pointer',
                          fontWeight: 'var(--font-weight-semibold)',
                          fontSize: 'var(--font-size-sm)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'var(--color-primary)',
                          textDecoration: 'underline',
                        }}
                      >
                        {formatDocumentDisplayName({
                          cmsNumber: doc.cms_number,
                          originalFileName: doc.original_file_name,
                          documentType: doc.document_type,
                        })}
                      </button>
                      <span
                        style={{
                          padding: '1px 8px',
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--color-gray-100)',
                          color: 'var(--color-gray-600)',
                          fontSize: 'var(--font-size-xs)',
                        }}
                      >
                        Confidential
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-gray-500)',
                      }}
                    >
                      {[
                        (doc as any).file_number || doc.cms_number,
                        (doc as any).subject,
                        doc.folder_type,
                        doc.document_type,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-gray-400)',
                        marginTop: '2px',
                      }}
                    >
                      Uploaded by {doc.uploaded_by_name} ·{' '}
                      {format(new Date(doc.created_at), 'dd MMM yyyy')}
                    </p>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    {accessStatus !== 'none' ? (
                      <AccessBadge status={accessStatus} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setSelectedDocument(doc)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-2)',
                          padding: 'var(--spacing-2) var(--spacing-4)',
                          background: 'var(--color-primary)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          fontWeight: 'var(--font-weight-medium)',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          transition: 'opacity var(--transition-fast)',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.opacity = '0.85')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.opacity = '1')
                        }
                      >
                        <Send size={14} /> Request Access
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 'var(--spacing-1)',
              }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!pagination.hasPrevPage}
                style={{
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-md)',
                  background: 'white',
                  cursor: pagination.hasPrevPage ? 'pointer' : 'not-allowed',
                  opacity: pagination.hasPrevPage ? 1 : 0.4,
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Previous
              </button>
              <span
                style={{
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-gray-600)',
                }}
              >
                Page {page} of {pagination.totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setPage((p) =>
                    Math.min(pagination.totalPages, p + 1)
                  )
                }
                disabled={!pagination.hasNextPage}
                style={{
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-md)',
                  background: 'white',
                  cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                  opacity: pagination.hasNextPage ? 1 : 0.4,
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedDocument && (
        <RequestAccessModal
          document={selectedDocument}
          onClose={closeRequestModal}
          onSuccess={() =>
            queryClient.invalidateQueries({ queryKey: ['confidential-docs'] })
          }
        />
      )}
    </div>
  );
};

export default AccessRequestPage;
